import { parse } from 'query-string';
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import api from '../../../api';
import { ILoginResponse, ILoginSuccess } from '../../../api/sessions';
import { ISessionState } from '../../../entities/auth/session/reducers';
import { logging } from '../../../logging';
import { LOGIN_ROUTES } from './constants';
import { LoginCheckInbox } from './LoginCheckInbox';
import { LoginComplete } from './LoginComplete';
import { LoginConfirm } from './LoginConfirm';
import { LoginEmail } from './LoginEmail';
import { LoginEmailHelp } from './LoginEmailHelp';
import { LoginError } from './LoginError';
import { LoginPassword } from './LoginPassword';

const PASSWORD_REQUIRED = 'USER_PASSWORD_AUTH';

const emailParam = (search: string) => {
	const params = parse(search);
	return typeof params.email === 'string' ? params.email : '';
};

export interface ILoginProps {
	currentSession: ISessionState;
	onSubmitEmailAndPassword: (email: string, password: string) => Promise<void>;
	onConfirmPasswordlessLogin: (email: string, session: string, code: string) => Promise<void>;
	onResetPassword: (email: string) => Promise<void>;
}

const throwError = (msg: string) => { throw new Error(msg); };
const isSuccess = (r: ILoginResponse): r is ILoginSuccess => !Object.keys(r).includes('errorType');
const verifyResponse = (r: ILoginResponse) => isSuccess(r) ? r : throwError(r.errorMessage);

export const Login: React.FC<ILoginProps> = (props) => {
	const location = useLocation();
	const navigate = useNavigate();
	const [email, setEmail] = React.useState(emailParam(location.search));
	const [error, setError] = React.useState<Error | null>(null);
	const [password, setPassword] = React.useState('');
	const [loading, setLoading] = React.useState(false);

	React.useEffect(() => {
		if (!(Object.values(LOGIN_ROUTES) as string[]).includes(location.pathname)) {
			navigate(LOGIN_ROUTES.EMAIL);
		}
	}, []);

	const submitEmailAddress = async (to: string) => {
		setLoading(true);
		try {
			logging.info(`Request login challenge for ${to}`);
			const result = verifyResponse(await api.sessions.login(to));

			if (result.ChallengeName === PASSWORD_REQUIRED) {
				logging.info(`${to} has a password`);
				navigate(LOGIN_ROUTES.PASSWORD);
			} else {
				logging.info(`${to} is passwordless`);
				navigate(LOGIN_ROUTES.CHECK_INBOX);
			}
		} catch (e) {
			logging.error(`Error requesting login challenge for ${to}`, e);
			if ((e.message || '').toLowerCase().includes('password reset required')) {
				setError(e);
			} else {
				navigate(LOGIN_ROUTES.ERROR);
			}
		} finally {
			setLoading(false);
		}
	};

	const resetPassword = async () => {
		setLoading(true);
		try {
			logging.info(`Resetting password for ${email}`);
			await props.onResetPassword(email);
		} catch (e) {
			logging.error(`Error resetting password for ${email}`, e);
			setError(e);
		} finally {
			setLoading(false);
		}
	};

	switch (location.pathname) {
		case LOGIN_ROUTES.EMAIL:
			return <LoginEmail
				initialEmail={email}
				loading={loading}
				error={error}
				onChange={setEmail}
				focusInput
				onSubmit={() => submitEmailAddress(email)}
				onResetPassword={() => resetPassword()}
			/>;
		case LOGIN_ROUTES.CHECK_INBOX:
			if (!email) { navigate(LOGIN_ROUTES.EMAIL); return null; }
			return <LoginCheckInbox email={email} />;
		case LOGIN_ROUTES.EMAIL_HELP:
			if (!email) { navigate(LOGIN_ROUTES.EMAIL); return null; }
			return <LoginEmailHelp loading={loading} onSubmit={() => submitEmailAddress(email)} />;
		case LOGIN_ROUTES.LOGIN_CONFIRM:
			return <LoginConfirm
				loading={loading}
				search={location.search}
				onRequestNewEmail={async (to) => {
					setEmail(to);
					logging.info(`Sending fresh email to ${to}`);
					await submitEmailAddress(to);
					navigate(LOGIN_ROUTES.CHECK_INBOX);
				}}
				onConfirmLogin={async (submitEmail, session, code) => {
					setLoading(true);
					logging.info(`Confirming passwordless login for ${submitEmail}`);
					try {
						await props.onConfirmPasswordlessLogin(submitEmail, session, code);
						navigate(LOGIN_ROUTES.COMPLETE);
					} catch (e) {
						navigate(LOGIN_ROUTES.ERROR);
					} finally {
						setLoading(false);
					}
				}}
			/>;
		case LOGIN_ROUTES.PASSWORD:
			if (!email) { navigate(LOGIN_ROUTES.EMAIL); return null; }
			return <LoginPassword
				initialPassword={password}
				loading={loading}
				onChange={setPassword}
				error={error}
				focusInput
				onSubmit={async () => {
					setLoading(true);
					try {
						logging.info(`Submitting email & password for ${email}`);
						await props.onSubmitEmailAndPassword(email, password);
						navigate(LOGIN_ROUTES.COMPLETE);
					} catch (e) {
						logging.error(`Error submitting email & password for ${email}`, e);
						setError(e);
					} finally {
						setLoading(false);
					}
				}}
				onResetPassword={() => resetPassword()}
			/>;
		case LOGIN_ROUTES.COMPLETE:
			return <LoginComplete currentSession={props.currentSession} />;
		case LOGIN_ROUTES.ERROR:
			return <LoginError />;
		default:
			return null;
	}
};
